import { useEffect, useRef, useState } from "react";
import { VIDEOS, CAPA, PREVIA, NA_PRATELEIRA, type Video } from "~/lib/videos";

/**
 * A prateleira de vídeos.
 *
 * Capa parada não vende vídeo: o produto aqui é a animação, então cada
 * card roda um trecho mudo de 4 segundos (~40kB). A tira anda sozinha e
 * para quando o ponteiro ou o teclado entram nela, para dar tempo de ler.
 *
 * Só toca o que está na tela, e nada toca com a aba escondida — vídeo
 * rodando atrás gasta bateria à toa.
 */
export function FaixaVideos({ pular }: { pular?: string }) {
  // Teto na prateleira: com 80 videos seriam 80 previas carregando.
  const lista = VIDEOS.filter((v) => v.slug !== pular).slice(0, NA_PRATELEIRA);
  const trilho = useRef<HTMLDivElement>(null);
  const [pausado, setPausado] = useState(false);

  useEffect(() => {
    const raiz = trilho.current;
    if (!raiz) return;

    const semMovimento = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (semMovimento) return;

    const videos = [...raiz.querySelectorAll("video")];
    const tocar = (v: HTMLVideoElement, sim: boolean) => {
      if (sim) void v.play().catch(() => undefined);
      else v.pause();
    };

    const obs = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          tocar(e.target as HTMLVideoElement, e.isIntersecting);
        }
      },
      { threshold: 0.25 },
    );
    videos.forEach((v) => obs.observe(v));

    const aoTrocarAba = () => {
      if (document.hidden) videos.forEach((v) => v.pause());
    };
    document.addEventListener("visibilitychange", aoTrocarAba);

    return () => {
      obs.disconnect();
      document.removeEventListener("visibilitychange", aoTrocarAba);
    };
  }, []);

  return (
    <>
      <div
        className="jg-marquee"
        ref={trilho}
        data-pausado={pausado ? "" : undefined}
      >
        <div className="jg-marquee-trilho">
          {lista.map((v) => (
            <Card key={v.slug} video={v} />
          ))}
          {/* A cópia existe só para o laço não ter emenda visível. */}
          {lista.map((v) => (
            <Card key={`copia-${v.slug}`} video={v} copia />
          ))}
        </div>
      </div>

      {/* Conteúdo que anda sozinho precisa de um jeito de parar que não
          dependa de passar o mouse — no celular não existe hover. */}
      <div className="jg-shell mt-5">
        <button
          type="button"
          className="text-sm text-jg-fg-muted underline decoration-jg-border underline-offset-4 transition-colors hover:text-jg-fg hover:decoration-jg-accent"
          aria-pressed={pausado}
          onClick={() => setPausado((p) => !p)}
        >
          {pausado ? "retomar a prateleira" : "pausar a prateleira"}
        </button>
      </div>
    </>
  );
}

/**
 * O vídeo em destaque. Mesma regra dos outros: mudo, em laço, e só roda
 * quando está na tela e quem olha não pediu menos movimento.
 */
export function VideoDestaque({
  src,
  poster,
  titulo,
}: {
  src: string;
  poster: string;
  titulo: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const obs = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) void v.play().catch(() => undefined);
        else v.pause();
      },
      { threshold: 0.3 },
    );
    obs.observe(v);
    return () => obs.disconnect();
  }, []);

  return (
    <span className="jg-faixa-moldura block">
      <video
        ref={ref}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={`Trecho do vídeo: ${titulo}`}
        className="block aspect-[9/16] w-full object-cover"
      />
    </span>
  );
}

function Card({ video, copia }: { video: Video; copia?: boolean }) {
  return (
    <figure className="jg-faixa-item m-0" aria-hidden={copia || undefined}>
      <span className="jg-faixa-moldura block">
        <video
          src={PREVIA(video.slug)}
          poster={CAPA(video.slug)}
          muted
          loop
          playsInline
          preload="none"
          aria-label={`Trecho do vídeo: ${video.titulo}`}
          className="block aspect-[9/16] w-full object-cover"
        />
      </span>
      <figcaption className="mt-3">
        <span className="block text-sm font-medium leading-snug">
          {video.titulo}
        </span>
        <span className="mt-1 block text-sm leading-snug text-jg-fg-muted">
          {video.resumo}
        </span>
      </figcaption>
    </figure>
  );
}
